import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../../utils/textUtils';
import { FORM_TYPE, DATE_FIELD, DATE_CHANGE_STATUS } from '../../../../../utils/formDateChangeConstants';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const FORM_TYPE_SEVERITY = {
    [FORM_TYPE.VAF]: 'info',
    [FORM_TYPE.PSI]: 'warning',
    [FORM_TYPE.CVRF]: 'help'
};

const DATE_FIELD_LABEL_KEY = {
    [DATE_FIELD.VEHICLE_ACCEPT_DATE]: 'formDateChangeRequest.dateField.vehicleAcceptDate',
    [DATE_FIELD.STOCK_IN_DATE]: 'formDateChangeRequest.dateField.stockInDate',
    [DATE_FIELD.INSPECTION_DATE]: 'formDateChangeRequest.dateField.inspectionDate',
    [DATE_FIELD.VEHICLE_REGISTRATION]: 'formDateChangeRequest.dateField.vehicleRegistration'
};

const FORM_TYPE_LABEL_KEY = {
    [FORM_TYPE.VAF]: 'formDateChangeRequest.formType.vaf',
    [FORM_TYPE.PSI]: 'formDateChangeRequest.formType.psi',
    [FORM_TYPE.CVRF]: 'formDateChangeRequest.formType.cvrf'
};

const WarrantyDateChangeApprovalTable = ({ items, lazyParams, totalRecords, loading, onPage, currentStatus, canApproveDateChange, onApprove, onReject, approvingId }) => {
    const { th } = useLanguage();

    const formTypeBodyTemplate = (rowData) => <Tag value={th(FORM_TYPE_LABEL_KEY[rowData.formType]) || rowData.formType} severity={FORM_TYPE_SEVERITY[rowData.formType] || 'info'} />;

    const dateFieldBodyTemplate = (rowData) => th(DATE_FIELD_LABEL_KEY[rowData.dateField]) || '-';

    const dateChangeBodyTemplate = (rowData) => (
        <span>
            <span className="text-500 line-through mr-2">{formatDateOnly(rowData.oldDate)}</span>
            <i className="pi pi-arrow-right text-500 mr-2" style={{ fontSize: '0.75rem' }} />
            <span className="font-medium">{formatDateOnly(rowData.newDate)}</span>
        </span>
    );

    const requestedByBodyTemplate = (rowData) => (
        <div>
            <div className="font-medium">{rowData.requestedName || '-'}</div>
            <div className="text-500 text-sm">{formatDateOnly(rowData.requestedDate)}</div>
        </div>
    );

    const lastColumnHeader =
        currentStatus === DATE_CHANGE_STATUS.SENT_FOR_APPROVAL ? th('formDateChangeRequest.table.action') : currentStatus === DATE_CHANGE_STATUS.RETURN ? th('formDateChangeRequest.table.rejectedBy') : th('formDateChangeRequest.table.approvedBy');

    const lastColumnBody = (rowData) => {
        if (currentStatus === DATE_CHANGE_STATUS.SENT_FOR_APPROVAL) {
            if (!canApproveDateChange) return '-';
            return (
                <div className="flex gap-2">
                    <Button icon="pi pi-check" label={th('formDateChangeRequest.table.approve')} className="p-button-success p-button-sm" loading={approvingId === rowData.id} onClick={() => onApprove(rowData)} />
                    <Button icon="pi pi-times" label={th('formDateChangeRequest.table.reject')} className="p-button-danger p-button-sm p-button-outlined" onClick={() => onReject(rowData)} />
                </div>
            );
        }

        return (
            <div>
                <div className="font-medium">{rowData.approvedName || '-'}</div>
                <div className="text-500 text-sm">{formatDateOnly(rowData.approvedDate)}</div>
                {currentStatus === DATE_CHANGE_STATUS.RETURN && rowData.returnMsg && <div className="text-red-600 text-sm mt-1">{createTruncatedTextForTable(rowData.returnMsg, 40)}</div>}
            </div>
        );
    };

    return (
        <DataTable
            value={items}
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows}
            totalRecords={totalRecords}
            onPage={onPage}
            loading={loading}
            emptyMessage={th('formDateChangeRequest.table.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            dataKey="id"
            tableStyle={{ minWidth: '70rem' }}
            showGridlines
            stripedRows
            className="p-datatable-gridlines"
        >
            <Column body={formTypeBodyTemplate} header={th('formDateChangeRequest.table.formType')} style={{ minWidth: '90px' }} />
            <Column field="chassisNo" header={th('formDateChangeRequest.table.chassisNo')} sortable style={{ minWidth: '160px' }} />
            <Column body={dateFieldBodyTemplate} header={th('formDateChangeRequest.table.dateField')} style={{ minWidth: '160px' }} />
            <Column body={dateChangeBodyTemplate} header={th('formDateChangeRequest.table.dateChange')} style={{ minWidth: '220px' }} />
            <Column body={requestedByBodyTemplate} header={th('formDateChangeRequest.table.requestedBy')} style={{ minWidth: '170px' }} />
            <Column field="description" header={th('formDateChangeRequest.table.description')} style={{ minWidth: '200px' }} body={(rowData) => (rowData.description ? createTruncatedTextForTable(rowData.description, 40) : '-')} />
            <Column body={lastColumnBody} header={lastColumnHeader} style={{ minWidth: '200px' }} />
        </DataTable>
    );
};

export default WarrantyDateChangeApprovalTable;
